import { useState, useEffect } from 'react';
import { useListStockSummaryQuery } from '../apis/stockManagement.api';
import { getTaxabilityLabel } from '../constants/common';
import { useDispatch, useSelector } from 'react-redux';
import { setDbDateRange } from '../../../redux/reducers/DashboardSlice';
import { useNavigate } from 'react-router-dom';
import { updateTitle } from '../../../redux/reducers/titleSlice';
import { 
    StockItem, 
    StockTotals, 
    UseClosingStockSummaryReturn,
    DateRange,
    ColumnFilter,
    SelectedItem
} from '../types';
import { ProcessedStockSummary } from '../types/graphql';

const createData = (
  sl: number,
  id: string,
  itemCode: string,
  itemName: string,
  unit: string,
  HSN: string,
  taxability: string,
  gstRate: number,
  openingStockAmt: number,
  openingStockQty: number,
  inStockAmount: number,
  inStockQty: number,
  outStockAmount: number,
  outStockQty: number,
  closingStockAmount: number,
  closingStockQty: number
): StockItem => {
   const COGS = Number(openingStockAmt || 0) + Number(inStockAmount || 0) - Number(closingStockAmount || 0);
   const profitLoss = Number(outStockAmount || 0) - COGS;
   const marginPercentage = outStockAmount !== 0 ? Number(((profitLoss / outStockAmount) * 100).toFixed(2)) : 0;

  return {
    sl,
    id,
    itemCode,
    itemName,
    unit,
    HSN,
    taxability,
    gstRate,
    openingStockAmt,
    openingStockQty,
    inStockAmount,
    inStockQty,
    outStockAmount,
    outStockQty,
    closingStockAmount: Number(closingStockAmount || 0),
    closingStockQty: Number(closingStockQty || 0),
    cogs: COGS,
    pandl: profitLoss,
    margin: marginPercentage,
  };
};

const calculateTotals = (rows: StockItem[]): StockTotals => {
  return rows.reduce(
    (acc, row) => {
      acc.totalOpeningStockAmt += row.openingStockAmt;
      acc.totalOpeningStockQty += row.openingStockQty;
      acc.totalInStockAmt += row.inStockAmount;
      acc.totalInStockQty += row.inStockQty;
      acc.totalOutStockAmt += row.outStockAmount;
      acc.totalOutStockQty += row.outStockQty;
      acc.closingStockAmount += row.closingStockAmount;
      acc.closingStockQty += row.closingStockQty;
      return acc;
    },
    {
      totalOpeningStockAmt: 0,
      totalOpeningStockQty: 0,
      totalInStockAmt: 0,
      totalInStockQty: 0,
      totalOutStockAmt: 0,
      totalOutStockQty: 0,
      closingStockAmount: 0,
      closingStockQty: 0,
    }
  );
};

export function useClosingStockSummary(includeOpeningBalance: boolean = true): UseClosingStockSummaryReturn {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [displayedRows, setDisplayedRows] = useState<StockItem[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totals, setTotals] = useState<StockTotals>({
    totalOpeningStockAmt: 0,
    totalOpeningStockQty: 0,
    totalInStockAmt: 0,
    totalInStockQty: 0,
    totalOutStockAmt: 0,
    totalOutStockQty: 0,
    closingStockAmount: 0,
    closingStockQty: 0,
  });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState<boolean>(false);

  const { report } = useSelector((state: any) => state?.DashboardDataReducer) || {};

  const offset = page * rowsPerPage;

  const VARIABLE = {
    starttimestamp: Number(report?.startTimeStamp) || 0,
    endtimestamp: Number(report?.endTimeStamp) || 0,
    limit: rowsPerPage,
    offset: offset,
    ...(includeOpeningBalance !== undefined && { openingbalance: includeOpeningBalance ? 1 : 0 }),
  };

  const isvariablesValid = VARIABLE.starttimestamp > 0 && VARIABLE.endtimestamp > 0;

  const { data, error, isLoading, isFetching } = useListStockSummaryQuery(
    { input: VARIABLE },
    { skip: !isvariablesValid, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    setPage(0);
    setHasMoreData(false);
  }, [report?.startTimeStamp, report?.endTimeStamp, includeOpeningBalance]);

  useEffect(() => {
    if (!data) {
      setDisplayedRows([]);
      setTotalRows(0);
      setHasMoreData(false);
      setTotals({
        totalOpeningStockAmt: 0,
        totalOpeningStockQty: 0,
        totalInStockAmt: 0,
        totalInStockQty: 0,
        totalOutStockAmt: 0,
        totalOutStockQty: 0,
        closingStockAmount: 0,
        closingStockQty: 0,
      });
      return;
    }

    const rows = data.map((item: ProcessedStockSummary, index: number) =>
      createData(
        offset + index + 1, 
        item?.itemid,
        item.itemcode,
        item.itemname,
        item.unit,
        item.hsn,
        getTaxabilityLabel(item.taxability ? Number(item.taxability) : 0),
        item.gstrate,
        item.openingamt,
        item.openingqty,
        item.purchaseamt,
        item.purchaseqty,
        item.salesamt,
        item.salesqty,
        item.closingamt,
        item.closingqty
      )
    );
    
    setDisplayedRows(rows);
    
    const hasMore = data.length === rowsPerPage;
    setHasMoreData(hasMore);
    
    if (hasMore) {
     
      setTotalRows(offset + data.length + 1);
    } else {
      const exactTotal = offset + data.length;
      setTotalRows(exactTotal);
    }
    
  
    setTotals(calculateTotals(rows));
  }, [data, page, rowsPerPage, offset]);

  const handleChangePage = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any): void => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSelectPeriod = (range: DateRange): void => {
    dispatch(
      setDbDateRange({
        outerKey: 'report',
        innerKey: 'range',
        value: range,
      })
    );
  };

  const handleNavigateItemwiseView = (params: SelectedItem): void => {
    dispatch(updateTitle(params.itemname));
    navigate('/inventory-transaction-summary', {
      state: { 
        itemid: params.itemid, 
        itemname: params.itemname, 
        itemcode: params.itemcode,
        unit: params.unit
      },
    });
  };

  const dateBetweenFn = (row: any, columnId: string, filterValue: any): boolean => {
    if (!filterValue || (!filterValue.from && !filterValue.to)) return true;

    const raw = row.getValue(columnId);
    if (!raw) return false;

    let d = new Date(raw);
    if (isNaN(d.getTime())) return false;

    const fromD = filterValue.from ? new Date(filterValue.from) : null;
    const toD = filterValue.to ? new Date(filterValue.to) : null;

    const fromOk = !fromD || d >= fromD;
    const toOk = !toD || d <= toD;

    return fromOk && toOk;
  };

  const containsText = (row: any, columnId: string, value: string): boolean => {
    const v = row.getValue(columnId);
    return String(v ?? '')
      .toLowerCase()
      .includes(String(value ?? '').toLowerCase());
  };

  return {
    page,
    rowsPerPage,
    displayedRows,
    totalRows,
    totals,
    range: report?.range || { startTimeStamp: '', endTimeStamp: '' },
    isLoading: isLoading || isFetching,
    expandedRows,
    columnFilters,
    showSearch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSelectPeriod,
    handleNavigateItemwiseView,
    dateBetweenFn,
    containsText,
    setExpandedRows,
    setColumnFilters,
    setShowSearch,
  };
}